import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  forkJoin,
  Observable,
  of,
  throwError,
  zip,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mapTo,
  switchMap,
  switchMapTo,
  take,
  tap,
} from 'rxjs';

import { ContactQuery, ContactStore, CurrentUserQuery } from '.';
import { EmployeesService } from '../default/employees/services/employee.service';
import { OverviewService } from '../default/overview/services/overview.service';
import { ConfigurationService } from '../shared/services/configuration.service';
import { ProfileService } from '../shared/services/profile.service';
import { SecurityService } from '../shared/services/security.service';
import { UtilityService } from '../shared/services/utility.service';
import { TermsOfUseService } from '../terms-of-use/services/terms-of-use.service';
import { BenefitPlanQuery } from './benefit-plan';
import { CustomizationService } from '@app/shared/services/customization.service';

@Injectable({
  providedIn: 'root',
})
export class PrefetchService {
  private benefitPlanQuery = inject(BenefitPlanQuery);
  private configurationService = inject(ConfigurationService);
  private contactQuery = inject(ContactQuery);
  private contactStore = inject(ContactStore);
  private currentUserQuery = inject(CurrentUserQuery);
  private customizationService = inject(CustomizationService);
  private employeesService = inject(EmployeesService);
  private overviewService = inject(OverviewService);
  private profileService = inject(ProfileService);
  private router = inject(Router);
  private securityService = inject(SecurityService);
  private termsOfUseService = inject(TermsOfUseService);
  private utilityService = inject(UtilityService);

  public prefetchDataOnAuthentication(): Observable<boolean> {
    return this.currentUserQuery.isLoggedIn$.pipe(
      filter((isLoggedIn) => isLoggedIn),
      take(1),
      switchMap(() => {
        const clientId = this.currentUserQuery.getClientIdFromToken();
        const isFullyLoggedIn = this.currentUserQuery.isFullyLoggedIn();
        const refreshedData$ = isFullyLoggedIn && clientId ? this.prefetchData(clientId) : this.loadContactsAndClientSummary();
        return refreshedData$.pipe(map(() => true));
      })
    );
  }

  private loadContactsAndClientSummary(): Observable<void> {
    return forkJoin([this.profileService.loadContacts(this.currentUserQuery.getEmailAddress()).pipe(catchError(() => of(null)))]).pipe(mapTo(null));
  }

  private prefetchData(clientId: string): Observable<void> {
    return zip(this.profileService.loadContactsForClient(clientId), this.profileService.loadClient(clientId)).pipe(
      switchMap(() => this.contactQuery.selectByEmail(this.currentUserQuery.getEmailAddress())),
      filter((contact) => !!contact && this.contactQuery.getActiveId() !== contact.id),
      tap((contact) => this.contactStore.setActive(contact.id)),
      switchMap(() => this.prefetchHighPriorityData(clientId)),
      catchError(async (error: unknown) => {
        if (typeof error === 'string' && error.indexOf('401 OK') > 0) {
          this.utilityService.setApiErrorMessage('No authorization exists. Please see your account\'s primary contact for access.');
        }
        await this.router.navigate(['/logout']);
        return throwError(error);
      }),
      mapTo(null)
    );
  }

  private prefetchHighPriorityData(clientId: string): Observable<void> {
    return zip(
      this.securityService.loadGroupPermissions(this.currentUserQuery.getUserGroupsFromToken()),
      this.configurationService.loadDivisions(clientId),
      this.configurationService.loadPayroll(clientId),
      this.overviewService.loadBenefitPlans(clientId),
      this.customizationService.getCustomizationByClientId(clientId)
    ).pipe(
      switchMapTo(zip(this.benefitPlanQuery.selectAllWhenLoaded(), this.termsOfUseService.getTermsOfUseDocument())),
      switchMap(([benefitPlans, termsOfUseDocument]) => {
        const benefitPlansGroupedByEffectiveDate = this.employeesService.getBenefitPlansGroupedByEffectiveDate(benefitPlans);
        const benefitPlanIds = benefitPlans.map((bp) => bp.id);
        return zip(this.employeesService.loadBenefitPlanFundingSources(benefitPlanIds), this.employeesService.loadPayrollSchedulesByPlan(benefitPlansGroupedByEffectiveDate), this.termsOfUseService.getSignedDocumentActions(termsOfUseDocument.id));
      }),
      mapTo(null)
    );
  }
}
